import React from "react";

import { Box, Paper, Typography, useTheme } from "@mui/material";

export interface ProjectStatsStripProps {
  totalResources: number;
  runningResources: number;
  healthPct: number;
  healthyCount: number;
  totalApps: number;
  runningApps: number;
  mtdCost: string;
  mtdCostQuality: string;
  lastActivityLabel: string;
  lastActivitySource: string;
}

interface KpiCardProps {
  value: string;
  label: string;
  trend: string;
  trendColor?: "success" | "warning" | "default";
}

function KpiCard({
  value,
  label,
  trend,
  trendColor = "default",
}: KpiCardProps): React.ReactElement {
  const theme = useTheme();
  const color =
    trendColor === "success"
      ? theme.palette.success.main
      : trendColor === "warning"
        ? theme.palette.warning.main
        : theme.palette.text.secondary;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 1.5,
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="caption" sx={{ color, display: "block", mt: 0.25 }}>
        {trend}
      </Typography>
    </Paper>
  );
}

/**
 * KPI grid — 4 cards in a responsive grid matching the mockup's
 * `rusha-kpi-grid` pattern.
 */
export function ProjectStatsStrip({
  totalResources,
  runningResources,
  healthPct,
  healthyCount,
  totalApps,
  runningApps,
  mtdCost,
  mtdCostQuality,
}: ProjectStatsStripProps): React.ReactElement {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(4, 1fr)",
        },
        gap: 2,
        mb: 3,
      }}
    >
      <KpiCard
        value={String(totalResources)}
        label="Running resources"
        trend={totalResources > 0 ? `${runningResources} running` : "—"}
        trendColor={runningResources > 0 ? "success" : "default"}
      />
      <KpiCard
        value={`${healthPct}%`}
        label="Health"
        trend={`${healthyCount} of ${totalResources + totalApps} healthy`}
        trendColor={healthPct === 100 ? "success" : "warning"}
      />
      <KpiCard
        value={String(totalApps)}
        label="Applications"
        trend={totalApps > 0 ? `${runningApps} running` : "0 running"}
        trendColor={runningApps > 0 ? "success" : "default"}
      />
      <KpiCard
        value={mtdCost}
        label="MTD cost"
        trend={
          mtdCostQuality === "cold"
            ? "warming up"
            : mtdCostQuality === "free"
              ? "free tier"
              : "—"
        }
        trendColor={
          mtdCostQuality === "cold"
            ? "warning"
            : mtdCostQuality === "free"
              ? "success"
              : "default"
        }
      />
    </Box>
  );
}
